/**
 * Service Worker registration utility.
 *
 * Registers the custom service worker and provides typed helpers
 * for communicating with it via the message channel.
 */

const SW_PATH = '/service-worker.js';

type SwVersionCallback = (version: string) => void;

let registration: ServiceWorkerRegistration | null = null;
let onVersionCallbacks: SwVersionCallback[] = [];

/**
 * Register the service worker.
 * Should be called once after the application mounts.
 *
 * Returns the registration, or null if service workers are not supported.
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.info('[PWA] Service workers not supported');
    return null;
  }

  try {
    registration = await navigator.serviceWorker.register(SW_PATH, {
      scope: '/',
      type: 'classic',
    });

    console.info('[PWA] Service worker registered', registration.scope);

    // Listen for updates
    registration.addEventListener('updatefound', () => {
      const installing = registration?.installing;
      if (installing) {
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed') {
            // New content is available
            console.info('[PWA] New service worker installed; waiting for activation');
          }
        });
      }
    });

    // If a registration already existed, check for updates
    if (registration.active) {
      registration.update().catch(() => {
        /* background update check — silent failure is fine */
      });
    }

    // Listen for swVersion responses
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.swVersion) {
        onVersionCallbacks.forEach((cb) => cb(event.data.swVersion));
        onVersionCallbacks = [];
      }
    });

    return registration;
  } catch (error) {
    console.error('[PWA] Service worker registration failed', error);
    return null;
  }
}

/**
 * Request the current service worker version.
 * Resolves with the version string from the active worker.
 */
export function getSwVersion(): Promise<string> {
  const controller = navigator.serviceWorker.controller;
  if (!controller) {
    return Promise.reject(new Error('No active service worker'));
  }

  return new Promise((resolve, reject) => {
    const channel = new MessageChannel();

    channel.port1.onmessage = (event) => {
      if (event.data?.swVersion) {
        resolve(event.data.swVersion);
      } else {
        reject(new Error('Invalid version response'));
      }
    };

    controller.postMessage(
      { action: 'GET_VERSION' },
      [channel.port2],
    );

    // Timeout after 5s
    setTimeout(() => reject(new Error('Version request timed out')), 5000);
  });
}

/**
 * Tell the service worker to clear all erpone-* caches.
 * Useful for manual "clear cache and refresh" actions.
 */
export async function clearSwCaches(): Promise<number> {
  const controller = navigator.serviceWorker.controller;
  if (!controller) {
    throw new Error('No active service worker');
  }

  return new Promise((resolve, reject) => {
    const channel = new MessageChannel();

    channel.port1.onmessage = (event) => {
      if (event.data?.cleared !== undefined) {
        resolve(event.data.cleared);
      } else {
        reject(new Error('Invalid clear response'));
      }
    };

    controller.postMessage(
      { action: 'CLEAR_CACHES' },
      [channel.port2],
    );

    setTimeout(() => reject(new Error('Clear caches request timed out')), 5000);
  });
}

/**
 * Unregister the service worker.
 */
export async function unregisterServiceWorker(): Promise<boolean> {
  if (registration) {
    const result = await registration.unregister();
    registration = null;
    return result;
  }

  if ('serviceWorker' in navigator) {
    const reg = await navigator.serviceWorker.getRegistration();
    if (reg) {
      return reg.unregister();
    }
  }

  return false;
}
